import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X } from "lucide-react";

export const FavoriteItemsCard = ({
  title, icon: Icon, description, suggestions, values, onChange,
  placeholder, cardTestId, inputTestId, buttonTestId,
  featured = false, destructive = false, badgeLabel = null
}) => {
  const [newItem, setNewItem] = useState("");
  const customItems = useMemo(
    () => values.filter((v) => !suggestions.includes(v)),
    [values, suggestions]
  );

  const toggle = (item) => {
    onChange(values.includes(item) ? values.filter((v) => v !== item) : [...values, item]);
  };

  const addCustom = () => {
    if (!newItem.trim()) return;
    onChange([...values, newItem.trim()]);
    setNewItem("");
  };

  const remove = (item) => onChange(values.filter((v) => v !== item));

  const activeClass = destructive
    ? "bg-destructive text-destructive-foreground hover:bg-destructive/80"
    : "bg-primary text-primary-foreground hover:bg-primary/80";
  const idleClass = destructive
    ? "border-border hover:border-destructive text-muted-foreground"
    : featured
      ? "border-primary/30 hover:border-primary text-primary/70"
      : "border-border hover:border-primary";

  return (
    <Card className={`bg-card ${featured ? "border-primary/30" : "border-border"}`} data-testid={cardTestId}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="w-5 h-5 text-primary" />
          {title}
          {badgeLabel && <Badge variant="outline" className="ml-2 text-primary border-primary">{badgeLabel}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((item) => (
            <Badge
              key={item}
              variant={values.includes(item) ? (destructive ? "destructive" : "default") : "outline"}
              onClick={() => toggle(item)}
              className={`cursor-pointer transition-all ${values.includes(item) ? activeClass : idleClass}`}
            >
              {item}
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder={placeholder}
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addCustom()}
            className="bg-transparent border-border focus:border-primary"
            data-testid={inputTestId}
          />
          <Button variant="outline" onClick={addCustom} className="border-border hover:border-primary" data-testid={buttonTestId}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {customItems.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground w-full mb-1">Custom:</span>
            {customItems.map((item) => (
              <Badge
                key={item}
                variant={destructive ? "destructive" : undefined}
                className={destructive ? "flex items-center gap-1" : "bg-primary/20 text-primary border-primary/30 flex items-center gap-1"}
              >
                {item}
                <button onClick={() => remove(item)} className={destructive ? "ml-1" : "ml-1 hover:text-destructive"}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
